import type { AuthUser } from "@/core/auth/session";

const FULL_ACCESS_PERMISSION = "system.full_access";
export const EMPLOYEE_ROLE_UPDATE_PERMISSION = "employees.role.update";
export const EMPLOYEE_PERSONAL_PROFILE_READ_PERMISSION =
  "employees.personal_profile.read";
export const BRANCH_READ_PERMISSION = "branches.read";
export const EMPLOYEE_ACCOUNT_READ_PERMISSIONS = [
  "employees.read_all",
  "employees.manage_scoped"
] as const;
const PROJECTS_MANAGE_PERMISSION = "projects.manage";
const PROJECT_COSTS_MANAGE_PERMISSION = "project_costs.manage";
const PROJECT_REVENUES_MANAGE_PERMISSION = "project_revenues.manage";
const PROJECT_FINANCE_MANAGE_PERMISSION = "project_finance.manage";

export function hasPermission(
  user: Pick<AuthUser, "permissions"> | null | undefined,
  permission: string
) {
  const permissions = new Set(user?.permissions ?? []);
  return (
    permissions.has(FULL_ACCESS_PERMISSION) ||
    permissions.has(permission) ||
    (permission.startsWith("projects.") &&
      permissions.has(PROJECTS_MANAGE_PERMISSION)) ||
    (permission.startsWith("project_costs.") &&
      permissions.has(PROJECT_COSTS_MANAGE_PERMISSION)) ||
    (permission.startsWith("project_revenues.") &&
      permissions.has(PROJECT_REVENUES_MANAGE_PERMISSION)) ||
    (permission.startsWith("project_finance.") &&
      permissions.has(PROJECT_FINANCE_MANAGE_PERMISSION))
  );
}

export function hasAnyPermission(
  user: Pick<AuthUser, "permissions"> | null | undefined,
  permissions: string[]
) {
  return permissions.some((permission) => hasPermission(user, permission));
}

export function canUpdateEmployeeRoles(
  user: Pick<AuthUser, "permissions"> | null | undefined
) {
  return hasPermission(user, EMPLOYEE_ROLE_UPDATE_PERMISSION);
}

export function canReadEmployeePersonalProfiles(
  user: Pick<AuthUser, "permissions"> | null | undefined
) {
  return hasPermission(user, EMPLOYEE_PERSONAL_PROFILE_READ_PERMISSION);
}

export function canReadEmployeeAccounts(
  user: Pick<AuthUser, "permissions"> | null | undefined
) {
  return EMPLOYEE_ACCOUNT_READ_PERMISSIONS.some((permission) =>
    hasPermission(user, permission)
  );
}

export function isSuperAdmin(
  user: Pick<AuthUser, "backendRoleKey"> | null | undefined
) {
  return user?.backendRoleKey === "SUPER_ADMIN";
}
